import type { Licence } from '../entitlement/licence';
import { Organization } from '../organizations/organization-types';
import { User, UserPlain } from '../users/types';

/**
 * UserGroupType describes the purpose of the use group.
 * This can be used to find a correct group, but it is not designed to change the behaviour of the group itself.
 */
export type UserGroupType =
  | 'system' // SpecialUserGroups
  | 'organization'
  | 'organization_admin'
  | 'organization_member'
  | 'company'
  | 'company_admin'
  | 'entitlement_holder' // No members or parent links; see EntitlementHolder in iam/entitlement/types
  | 'entitlement_policy_group' // A member policy's assignees; see EntitlementPolicyGroup in iam/entitlement/types
  | 'entitlement_custody_group'; // An organisation's owners; see EntitlementCustodyGroup in iam/entitlement/types

/** Policy and custody groups: their memberships change only through the entitlement assignment commands. */
export const assignmentGroupTypes: ReadonlySet<UserGroupType | undefined> = new Set<UserGroupType>([
  'entitlement_policy_group',
  'entitlement_custody_group',
]);

export interface UserGroup {
  id: number;
  name: string;
  description?: string;
  type?: UserGroupType;
  companyId?: string;
  parents?: UserGroup[];
  memberGroups?: UserGroup[];
  memberUsers?: User[];
  organization?: Organization;
  // Set on a user's own groups by the lineage loaders while licences are read from the record.
  licence?: Licence | null;
}

export interface UserGroupWithParents {
  id: number;
  parents?: UserGroupWithParents[];
}

export interface UserGroupPlain {
  id: number;
  name: string;
  description?: string;
  type?: UserGroupType;
  companyId?: string;
  parents?: UserGroupPlain[];
  memberGroups?: UserGroupPlain[];
  memberUsers?: UserPlain[];
  organization?: Organization;
  licence?: Licence | null;
}

// Special user groups should not containt or descent from clients' organizational user groups.
// NOTE: Make sure that validateUserGroupHierarchy passes after adding or removing SpecialUserGroupIds.
//       E.g. when demoting / removing a special user group, delete its children links and the whole group.
export enum SpecialUserGroupId {
  // Team is the primary organizational group for internal users. Membership is the role "every customer's data",
  // and it still enables the internal functions not yet moved to STAFF.
  TEAM = 2, // NOTE: Keep value in sync with Postgres functions!
  // Upright staff: internal tools that carry no customer data. TEAM is its child, so every team member is staff.
  STAFF = 11,
  // The people who give and take customer data grants. No parent or child groups; only its own members change its membership.
  CUSTOMER_DATA_GRANTERS = 12,
  // The people who launch, route and terminate Eevo GPU nodes (PLT-7657). No parent or child groups;
  // its membership changes by migration or in the database, never through the user-group tools.
  EEVO_GPU_OPERATORS = 14,
  ADMIN = 1, // Enables a few special abilities that might cause significant harm if triggered accidentally.
  TEST_USERS = 7, // Flags and hides internal test users that belong to client organizational user groups.

  // Special organizational user group for marking secret / NDA objects as internal to team
  NO_ONE_ELSE = 1423, // NOTE: Keep value in sync with Postgres functions!

  // An extra special user group that's considered to be the parent of all user groups where all users implicitly belong to and the implicit relations are not stored in DB.
  EVERYONE = 6, // NOTE: Keep value in sync with Postgres functions, including supplier_preparation_reader_groups!

  SIGNED_IN_USERS = 1665, // Keep in sync with supplier_preparation_reader_groups. Used for Finnhub data for signed-in users.

  // To be removed in favor of features
  FREE_TRIAL = 13,
  CLIENTS_AND_FREE_TRIAL_USERS = 1346,

  // Data access
  STANDARD_COVERAGE = 1369,
  EXTENDED_COVERAGE = 1392,
  // Carriers of the two CSRD data sets sold as packages (`EntitlementKey.COVERAGE_CSRD_DATA_SET_*`): empty
  // groups whose own `csrd` access rows are the data set. Created by a migration, filled by the restructuring script.
  CSRD_DATA_SET_EXTENDED = 15,
  CSRD_DATA_SET_BENCHMARK = 16,

  // Company client groups
  COMPANY_PLATFORM_FREE_CLIENTS = 1449,
  COMPANY_PLATFORM_STANDARD_CLIENTS = 1450,
  COMPANY_PLATFORM_SELF_SIGNUP_USERS = 1500,

  // Investor groups
  INVESTOR_STANDARD_CLIENTS = 1453,
  INVESTOR_FREE_CLIENTS = 1447,
  // TODO: Add group of investor free trial users here

  // Feature access (keep up to date with below featureUserGroupIds)
  CLIENTS = 3,
  ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS = 1358,
  EDIT_ALL_WEIGHTSETS = 1422,
  IMPACT_EXPORT = 1405,
  SDG_ALIGNMENT = 1387, // TODO: Delete, as we give access to everyone anyway
  EU_ELIGIBILITIES = 1401, // TODO: Delete, as we give access to everyone anyway
  EU_PAI = 1388,
  API = 1394,
  MICROWORK = 1436,
  MIXIN_ADVANCED_USERS = 1443,
  PRODUCT_MODEL_TEAM = 1444,
  CSRD_REASONINGS = 1709, // OH: Usergroups within this group can see the general reasoning of the CSRD product impact assessment
  GISE_STATEMENTS = 1710, // OH: Usergroups within this group can see statements and reasonings provided by GISE.
  CSRD_GROUPFOLIO_CONSTITUENT_EXPORT = 1795, // Enables the constituent export which – unlike the the aggregate export – is not available to all fund-level csrd customers.

  HIDE_FINANCIAL_MATERIALITY = 1796, // Hides FM subpages in UI

  // Grants NI/SDG/EU-t to unauthenticated visitors of the open platform. Added implicitly to the
  // anonymous access subject only (see subject-hierarchy.ts / getAllParentGroupsOfUser). Signed-in
  // users do NOT inherit it, so these lenses can be hidden per signed-in customer group.
  ANONYMOUS = 1452,
  // Grants the Net Impact lens. Newly created organizational groups are linked to it by default so
  // they keep the open-platform default; removing the link hides Net Impact for that group's users.
  NET_IMPACT_USERS = 1448,
}

// OA: These are temporary hard codings for customized accesses, and therefore should be kept to just this file.
// In order to make the possible migration to DB smooth, don't add ids that are not present in the 'user_group' DB-table.
export enum DirectFeatureAccessUserGroupId {
  TAALERI = 1007,
  AKTIA = 5,
  ARISAIG = 1365,
  IMPACTSIM = 1368,
  ENENTO = 1377,
  MANDATUM = 1006,
  TESI = 4,
  AALTO = 1480, // Given microwork capabilities at 29.6.2022 -VV & ML
  RLAM = 1441,
  JANNE_AUKIA_EXTERNAL_ACCESS = 1671,
  STATISTA = 1947, // Statista (trial) is given limited access to blanket editing
}

export const coverageUserGroupIds: SpecialUserGroupId[] = [
  SpecialUserGroupId.STANDARD_COVERAGE,
  SpecialUserGroupId.EXTENDED_COVERAGE,
];

// PLT-5110: user groups that grant entity access broadly to all (or a large
// shared population of) users rather than to one specific organization. Access
// that reaches an entity ONLY via one of these groups does NOT make that entity
// "owned" by the user's organization, so the chat "own results" resolver
// excludes such grants on both the front-page and the Edit/Owner axes (this is
// what keeps shared benchmark/library entities — previously pinned via these
// groups — out of a customer's "my results"). Composed from existing named
// constants (no raw magic-number list); extend it here if another broad-grant
// group (e.g. SIGNED_IN_USERS) starts surfacing shared entities as "own".
export const csrdDataSetCarrierGroupIds: SpecialUserGroupId[] = [
  SpecialUserGroupId.CSRD_DATA_SET_EXTENDED,
  SpecialUserGroupId.CSRD_DATA_SET_BENCHMARK,
];

export const sharedAccessUserGroupIds: SpecialUserGroupId[] = [
  SpecialUserGroupId.EVERYONE,
  ...coverageUserGroupIds,
  ...csrdDataSetCarrierGroupIds,
];

export const featureUserGroupIdsForTests: SpecialUserGroupId[] = [
  /**
   * Feature user groups ids that will be added to full license user group in backend test
   * Do not add organizational user groups here or it will break the tests
   */
  SpecialUserGroupId.SDG_ALIGNMENT,
  SpecialUserGroupId.ACCESS_TO_HOME_DOWNLOADS_AND_PRODUCTS,
  SpecialUserGroupId.EU_PAI,
  SpecialUserGroupId.EU_ELIGIBILITIES,
  // A paying customer holds the Net Impact lens through this group, the same way it holds SDG and
  // EU taxonomy through theirs. Without it the full-license test client reads as a customer whose
  // Net Impact lens has been switched off, and its net-impact scores come back masked.
  SpecialUserGroupId.NET_IMPACT_USERS,
  SpecialUserGroupId.IMPACT_EXPORT,
  SpecialUserGroupId.EDIT_ALL_WEIGHTSETS,
  SpecialUserGroupId.API,
  SpecialUserGroupId.CLIENTS,
  // SpecialUserGroupId.INPUT_PRODUCTS,
  // SpecialUserGroupId.INPUT_TARGETS_INSIGHTS,
];

export const nonOrganizationalUserGroupIdsWithUsers = [
  SpecialUserGroupId.STAFF,
  SpecialUserGroupId.CUSTOMER_DATA_GRANTERS,
  SpecialUserGroupId.EEVO_GPU_OPERATORS,
  SpecialUserGroupId.ADMIN,
  SpecialUserGroupId.TEST_USERS,
  SpecialUserGroupId.PRODUCT_MODEL_TEAM,
  SpecialUserGroupId.COMPANY_PLATFORM_SELF_SIGNUP_USERS,
  DirectFeatureAccessUserGroupId.JANNE_AUKIA_EXTERNAL_ACCESS,
];

export const testInvestorUserGroupId = 9;

export const allHardCodedUserGroups = Object.values(SpecialUserGroupId).filter(
  (v): v is SpecialUserGroupId => typeof v === 'number',
);

// As of 8/2023, used only for UI purposes, but included here for consistent placing of hardcoded user groups.
export const userGroupsWithOrganizationLinkingDisabled = allHardCodedUserGroups.filter(
  groupId => groupId !== SpecialUserGroupId.TEAM,
);

export enum GoogleAnalyticsUserGroup {
  TESI = 4,
  AKTIA = 5,
}

// OH TODO with Access 2.0: Fix user group hierarchy. The current hierarchy doesn't reflect a realistic hierarchy. For example,
// the EVERYONE usergroup inherits access rights from TEAM usergroup. Hence, the EVERYONE group has access to the same entities as the TEAM group.
export const userGroupHierarchyForTests: Array<{
  parent: number;
  child: number;
}> = [
  {
    parent: SpecialUserGroupId.EVERYONE,
    child: SpecialUserGroupId.CLIENTS,
  },

  {
    parent: SpecialUserGroupId.EVERYONE,
    child: SpecialUserGroupId.TEAM,
  },
  // TEAM's parents are copied to STAFF, which is TEAM's parent.
  {
    parent: SpecialUserGroupId.EVERYONE,
    child: SpecialUserGroupId.STAFF,
  },
  {
    parent: SpecialUserGroupId.STAFF,
    child: SpecialUserGroupId.TEAM,
  },

  {
    parent: SpecialUserGroupId.EVERYONE,
    child: SpecialUserGroupId.MICROWORK,
  },
  {
    parent: SpecialUserGroupId.EVERYONE,
    child: SpecialUserGroupId.ADMIN,
  },
  {
    parent: SpecialUserGroupId.CLIENTS,
    child: SpecialUserGroupId.FREE_TRIAL,
  },
];

export interface UserGroupInvitation {
  id: string;
  userGroupId: number;
  userEmail: string;
  welcomeMessage: string | null;
  firstName: string | null;
  lastName: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  expirationTime: string;
  deletedAt: string | null;
  intendedRole: string | null;
  intendedCompanyIds: string | null;
  // The member policy the invitee gets, at the revision the invitation was sent with.
  intendedPolicyId: string | null;
  intendedPolicyRevision: number | null;
}
